/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, Box, FormControl, InputLabel, FilledInput, Select, MenuItem } from '@mui/material';
import { CancelPresentationRounded, DeleteRounded, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './style';
import Datagrid from '../../ui/Datagrid';
import columns from './table_config';
import { renderCell } from '../../ui/Datagrid/table_config';
import ModalDialog from '../../ui/ModalDialog';
import { createDrillStringBitProperty, deleteDrillBitDetailsProperty, updateDrillStringBitDetailsProperty } from '../../../store/actions/wellsDrillString';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import { APP_ACTION_TYPES, WELLS_DRILL_STRING_BIT_TYPE } from '../../../utils/constants';
import SelectEditInputTextField from '../../ui/SelectEditInputTextField';
import DeleteConfirmationModal from '../../deleteConfirmationModal';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../../shared/config/hydraulics_constants';
import _ from 'lodash';
import { modifyMetricDataArray } from '../../../utils/getMetrics';
import { checkPermissions } from '../../../utils/checkPermissions';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const DrillStringBitDetails = ({
  handleClose,
  data,
  open,
  drillStringData
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const apiRef = useGridApiRef();
  const drillString = useSelector((state) => state.wellsDrillString);
  const [properties, setProperties] = useState([]);
  const [initialProperties, setInitialProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [bitSize, setBitSize] = useState('');
  const [bitType, setBitType] = useState('');
  const [bitTfa, setBitTfa] = useState('');
  const [disableAdd, setDisableAdd] = useState(false);
  const [bitSizeError, setBitSizeError] = useState(false);
  const [disableTfaField, setDisableTfaField] = useState(false);
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteNozzleAction, setDeleteNozzleAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState({});
  const [disableSave, setDisableSave] = useState(true);
  const {
    handleClose: unSavedDialogClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, handleClose);
  const { unit, convertUnitSystem } = useUnitConversion();
  const [isFocused, setIsFocused] = useState(false);

  const getModifiedDrillStringBitDetails = () => {
    const drillstringBitData = drillString?.data?.find(item => item._id === data?._id)?.bit_details;
    const modifiedDrillStringBitDetails = modifyMetricDataArray([{ ...drillstringBitData }], drillString.units?.bit_details, unit);
    return modifiedDrillStringBitDetails[0];
  };

  const minBitSize = drillString.units?.bit_details?.size?.min;
  const maxBitSize = drillString.units?.bit_details?.size?.max;
  const minNozzle = drillString.units?.bit_details?.nozzles?.min;
  const maxNozzle = drillString.units?.bit_details?.nozzles?.max;
  const [drillStringBitDetails, setDrillStringBitDetails] = useState(null);

  useEffect(() => {
    if (drillString?.loading) return;
    const modifiedData = getModifiedDrillStringBitDetails();
    setDrillStringBitDetails(modifiedData);
  }, [drillString]);

  useEffect(() => {
    if (drillStringBitDetails) {
      const setBitNozzles = drillStringBitDetails?.nozzles?.map((item, index) => ({
        number: index + 1,
        size: item,
        _id: generateRandom(),
      }));
      setProperties(setBitNozzles || []);
      setInitialProperties(setBitNozzles || []);
      setBitType(drillStringBitDetails?.type || '');
      setBitTfa(drillStringBitDetails?.tfa || '');
      setBitSize(drillStringBitDetails?.size || '');

      if (!drillStringBitDetails?.nozzles?.length && drillStringBitDetails?.tfa) {
        setDisableAdd(true);
      } else {
        setDisableAdd(false);
      }
    }
    return () => {
      setProperties([]);
      setInitialProperties([]);
    };
  }, [drillStringBitDetails]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const propertiesChanged = !_.isEqual(properties, initialProperties);
      const bitDetailsChanged =
      bitSize !== (drillStringBitDetails?.size || '') ||
      bitType !== (drillStringBitDetails?.type || '');
      const tfaChanged = !properties?.length && bitTfa !== (drillStringBitDetails?.tfa || '');
      if (propertiesChanged || bitDetailsChanged || tfaChanged) {
        setDisableSave(false);
      } else {
        setDisableSave(true);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [properties, bitSize, bitType, bitTfa]);

  useEffect(() => {
    if (properties?.length && properties[0]?.size === '') {
      const id = properties[0]?._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 1
        });
        apiRef.current.setCellMode(id, 'size', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'size');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddProperty = () => {
    const currentProperties = [...properties];
    const nextId = currentProperties?.length - 1;
    const newRow = {
      number: currentProperties?.length + 1,
      size: '',
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true
    };
    currentProperties.unshift(newRow);
    const updatedProperties = currentProperties?.map((item, index) => ({
      ...item,
      number: currentProperties?.length - index
    }));
    setProperties(updatedProperties);
  };

  useEffect(() => {
    if (properties?.length) {
      if (unit !== 'US') {
        const totalSize = properties.reduce(
          (sum, property) => sum + Math.PI * Math.pow(property?.size || 0, 2),
          0
        );
        const tfa = totalSize / 4;
        setBitTfa(tfa.toFixed(drillString?.units?.bit_details?.tfa?.decimals));
      } else {
        const totalSize = properties.reduce(
          (sum, property) => sum + Math.pow(property?.size || 0, 2),
          0
        );
        const tfa = totalSize / 1303.8;
        setBitTfa(tfa.toFixed(drillString?.units?.bit_details?.tfa?.decimals));
      }
      setDisableTfaField(true);
    }
    if (properties.length === 0 && drillStringBitDetails?.nozzles?.length !== 0) {
      setBitTfa('');
      setDisableTfaField(false);
    }
  }, [properties]);

  const handleDeleteBit = () => {
    dispatch(deleteDrillBitDetailsProperty(data?._id ? data?._id : drillString.drillStringId, data.wellsInfoId));
    setDisableTfaField(false);
    setDisableAdd(false);
    setDeleteAction(false);
  };

  const checkBitProperties = () => {
    const invalidFields = [];
    if (bitType === '') invalidFields.push('Type');
    if (bitSize === '') invalidFields.push('Size');
    if (bitSize !== '' && (Number(bitSize) < minBitSize || Number(bitSize) >= maxBitSize)) invalidFields.push(`Bit size must be between ${minBitSize} and ${maxBitSize} ${drillString?.units?.bit_details?.size?.label}`);
    if (bitTfa === '' || Number(bitTfa) === 0) invalidFields.push('TFA');
    const nozzles = properties
      .filter(item => item.size)
      .map(item => ({ size: item.size, id: item._id }));
    for (const nozzle of nozzles) {
      const numericNozzle = Number(nozzle.size);
      if (numericNozzle <= minNozzle || numericNozzle > maxNozzle || isNaN(numericNozzle)) {
        setInvalidRows(prev => ({ ...prev, [nozzle.id]: true }));
        invalidFields.push(`Nozzle value should be an integer between ${minNozzle} and ${maxNozzle}`);
        break;
      }
    }
    if (invalidFields.length === 0) {
      return true;
    } else {
      dispatch(
        enqueueSnackbar(
            `Please check the following details: ${invalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };
  const saveProperty = () => {
    if (checkBitProperties()) {
      const filtered = properties.filter(item => item?.size !== '');
      const existingNozzles = filtered?.filter(item => !item.isNewRow);
      const newNozzles = filtered.filter(item => item?.isNewRow)?.reverse();
      const nozzles = [...existingNozzles, ...newNozzles]?.map(item => item?.size);

      const body = {
        type: bitType,
        size: unit !== UNIT_SYSTEM.US ? convertUnitSystem(Number(bitSize), drillString?.units?.bit_details?.size?.conversionFactor) : Number(bitSize),
        tfa: unit !== UNIT_SYSTEM.US ? convertUnitSystem(Number(bitTfa), drillString?.units?.bit_details?.tfa?.conversionFactor) : Number(bitTfa),
        nozzles: unit !== UNIT_SYSTEM.US
          ? nozzles.map((e) => {
            return convertUnitSystem(Number(e), drillString?.units?.bit_details?.nozzles?.conversionFactor);
          })
          : nozzles
      };
      const drillStringDetailsObject = drillString?.data?.find(item => item._id === data?._id);
      if (!_.isEmpty(drillStringDetailsObject?.bit_details)) {
        if (!checkPermissions(APP_ACTION_TYPES.UPDATE, data?.permissions)) {
          dispatch(
            enqueueSnackbar(
              'You do not have permission to save changes.',
              'error',
              new Date().getTime() + Math.random()
            )
          );
          return false;
        }
        dispatch(updateDrillStringBitDetailsProperty(data?._id ? data?._id : drillString.drillStringId, body, data.wellsInfoId));
      } else {
        if (!checkPermissions(APP_ACTION_TYPES.CREATE, data?.permissions)) {
          dispatch(
            enqueueSnackbar(
              'You do not have permission to create.',
              'error',
              new Date().getTime() + Math.random()
            )
          );
          return false;
        }
        dispatch(createDrillStringBitProperty(data?._id ? data?._id : drillString.drillStringId, body, data.wellsInfoId));
      }
      setInitialProperties([...properties]);
    }
  };

  const updateData = (id, field, value) => {
    const numericValue = Number(value);
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          return {
            ...item,
            [field]: value === '' ? '' : numericValue
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const handleDeleteNozzles = (row) => {
    const filteredProperties = properties.filter((item) => item._id !== row._id);
    const updatedProperties = filteredProperties.map((item, index) => ({
      ...item,
      number: filteredProperties.length - index
    }));
    setProperties(updatedProperties);
    setDeleteNozzleAction(false);
    setDeleteRow({});
  };

  const closeHandler = () => {
    if (!disableSave) {
      unSavedDialogClose(false);
    } else {
      handleClose();
    }
  };
  const getModalTitle = () => {
    return (
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>{drillStringData?.row?.name?.trim()}, {data?.name || ''} {data?.name ? '@' : ''} {data.MD.toFixed(drillString?.units?.MD?.decimals)} {drillString?.units?.MD?.label}</Grid>
                    <Grid item>
                        <IconButton
                            onClick={closeHandler}
                        >
                            <CancelPresentationRounded fontSize='large' />
                        </IconButton>
                    </Grid>
                </Grid>
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderActions = ({ row }) => {
    return (
            <Grid container justifyContent="center">
                <IconButton
                    disabled={drillString.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, data?.permissions))}
                    variant="solid"
                    onClick={() => {
                      setDeleteNozzleAction(true);
                      setDeleteRow(row);
                    }}
                >
                    <DeleteRounded />
                </IconButton>
            </Grid>
    );
  };

  const handleBitSize = (value) => {
    const numericValue = Number(value);
    if (value === '' || (numericValue >= minBitSize && numericValue <= maxBitSize)) {
      setBitSize(value);
      setBitSizeError(false);
    } else {
      setBitSize(value);
    }
  };

  return (
        <ModalDialog
            title={getModalTitle()}
            isOpen={open}
            onClose={closeHandler}
            maxWidth='lg'
            dialogClass={classes.dialogModal}
        >
            <Grid container item>
                <Grid container item marginLeft='12px' marginTop='15px' marginBottom='5px'>
                    <Grid marginBottom='5px'>
                    <Typography variant="h5">Bit</Typography>
                    </Grid>
                    <Grid container item xs={12} spacing={5}>
                        <Grid item xs={3}>
                            <FormControl
                                variant="filled"
                                className={classes.inputField}
                                sx={{ bgcolor: 'transparent' }}
                                disabled={drillString?.loading || (drillStringBitDetails?.type && !checkPermissions(APP_ACTION_TYPES.UPDATE, data?.permissions))}
                            >
                                <InputLabel id={'signal-label'}>Select Bit Type</InputLabel>
                                <Select
                                    labelId={'signal-select'}
                                    id={'signal-select'}
                                    value={bitType}
                                    onChange={(e) => setBitType(e.target.value)}
                                    disableUnderline
                                >
                                    {WELLS_DRILL_STRING_BIT_TYPE?.map((item, idItem) => (
                                        <MenuItem key={`item_${item?.name}_${idItem}`} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3} display='flex' justifyContent='center' alignContent='center' alignItems='center'>
                            <FormControl
                                fullWidth
                                variant="filled"
                                className={classes.inputField}
                                sx={{ bgcolor: 'transparent' }}
                                disabled={drillString?.loading || (drillStringBitDetails?.size && !checkPermissions(APP_ACTION_TYPES.UPDATE, data?.permissions))}
                            >
                                <InputLabel fontWeight='bolder' fontSize='20px' htmlFor="outlined-adornment-select">
                                    Enter Bit Size
                                </InputLabel>
                                <FilledInput
                                    id="outlined-adornment-select"
                                    value={
                                      unit !== 'US'
                                        ? bitSize
                                        : isFocused || !bitSize
                                          ? bitSize
                                          : (() => {
                                              const decimals = drillString?.units?.bit_details?.size?.decimals;
                                              const num = Number(bitSize);

                                              if (isNaN(num)) return bitSize;

                                              return num
                                                .toFixed(decimals).replace(/\.?0+$/, '');
                                            })()
                                    }
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    disableUnderline
                                    sx={{
                                      border: bitSizeError ? '5px solid #ffcccc' : ''
                                    }}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (/^\d*\.?\d*$/.test(value)) {
                                        handleBitSize(value);
                                      }
                                    }}
                                />
                            </FormControl>
                            <Typography fontSize='12px' fontWeight='900' marginLeft='2%'>{drillString?.units?.bit_details?.size?.label}</Typography>
                        </Grid>
                        <Grid item xs={3} display='flex' justifyContent='center' alignContent='center' alignItems='center'>
                            <FormControl
                                fullWidth
                                variant="filled"
                                className={classes.inputField}
                                sx={{ bgcolor: 'transparent' }}
                            >
                                <InputLabel fontWeight='bolder' fontSize='20px' htmlFor="outlined-adornment-filter">
                                    TFA
                                </InputLabel>
                                <FilledInput
                                    id="outlined-adornment-filter"
                                    value={bitTfa}
                                    disableUnderline
                                    disabled={disableTfaField || drillString?.loading || (drillStringBitDetails?.tfa && !checkPermissions(APP_ACTION_TYPES.UPDATE, data?.permissions))}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (!isNaN(value) && value !== ' ') {
                                        setBitTfa(value);
                                        if (value === '' || Number(value) === 0) {
                                          setDisableAdd(false);
                                        } else {
                                          setDisableAdd(true);
                                        }
                                      }
                                    }}
                                />
                            </FormControl>
                            <Typography fontSize='12px' fontWeight='900' marginLeft='2%'>{drillString?.units?.bit_details?.tfa?.label}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <IconButton
                                variant="solid"
                                disabled={disableSave || drillString.loading || drillString?.loading}
                                fontSize="large"
                                onClick={() => {
                                  saveProperty();
                                }}
                            >
                                <SaveRounded />
                            </IconButton>
                            <IconButton
                                disabled={drillString.loading || !checkPermissions(APP_ACTION_TYPES.DELETE, data?.permissions)}
                                variant="solid"
                                onClick={() => {
                                  setDeleteAction(true);
                                }}
                            >
                                <DeleteRounded />
                            </IconButton>
                        </Grid>

                    </Grid>
                    <Grid item className={classes.customTool} xs={1} justifyContent="flex-start" marginTop='15px'>
                        <Box>
                            <Typography variant="h5">Nozzles</Typography>
                            <Typography variant='button' marginTop='5px' fontWeight='900' className={disableAdd || properties?.length > 23 ? classes.disableIcon : ''} onClick={handleAddProperty}>+ Add</Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Datagrid
                        apiRef={apiRef}
                        data={properties?.length ? properties : []}
                        loading={drillString.loading}
                        getRowId={(row) => row._id}
                        columns={columns(renderCell, renderActions, renderValueCell, drillString?.units?.bit_details, unit)}
                        disableStickyHeader={false}
                        autoHeight={false}
                        height={'50vh'}
                        sx={{
                          marginLeft: '-6px',
                          '.MuiDataGrid-cell': {
                            fontSize: '1rem',
                            fontWeight: 900
                          },
                          '.MuiDataGrid-columnHeader': {
                            fontSize: '1.2rem',
                            fontWeight: '900 !important'
                          }
                        }}
                        pageSize={100}
                        editMode="cell"
                        toolbar={{
                          options: {
                            columns: false,
                            filters: false,
                            density: false,
                            export: false
                          },
                        }}
                        onCellEditCommit={({ id, value, field }) => {
                          updateData(id, field, value);
                        }}
                        getRowClassName={(params) => {
                          return invalidRows[params.id] ? classes.invalidRow : '';
                        }}
                    />
                </Grid>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={handleDeleteBit}
            handleClose={setDeleteAction}
          />
        }
        {
          deleteNozzleAction && <DeleteConfirmationModal
            open={deleteNozzleAction}
            handleDelete={handleDeleteNozzles}
            handleClose={setDeleteNozzleAction}
            deleteRow={deleteRow}
          />
        }
        <UnsavedChangesModal />
            </Grid>
        </ModalDialog>
  );
};
DrillStringBitDetails.propTypes = {
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  open: PropTypes.bool.isRequired,
  drillStringData: PropTypes.any
};
DrillStringBitDetails.defaultProps = {
  data: {}
};
export default DrillStringBitDetails;
